import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { IconButton } from '@monorepo/shared-lib';
import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import * as React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

const SCOPE_DROPDOWN_MENU_CLASS = 'scope-dropdown-button-menu';

const Wrapper = styled.div<{ $inline?: boolean }>`
  display: flex;
  justify-content: flex-end;
  width: ${(p) => (p.$inline ? 'auto' : '100%')};

  .ant-space-compact-block {
    width: auto !important;
  }

  .ant-dropdown-button {
    border-radius: 7px;
    box-shadow: 0px 0px 10px 0px #0000000d;
  }

  .ant-dropdown-button > .ant-btn {
    height: 26px !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 12px !important;
    border: 2px solid var(--color-border, #dbeae3) !important;
    background-color: var(--color-surface-raised, #ffffff) !important;
    color: var(--color-text-dark, #042a2a) !important;
    box-shadow: none !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .ant-dropdown-button > .ant-btn:hover,
  .ant-dropdown-button > .ant-btn:focus {
    color: var(--color-text-dark, #042a2a) !important;
    border-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-dropdown-button > .ant-btn:first-child {
    border-radius: 7px 0 0 7px !important;
    padding: 0 10px !important;
    border-right-width: 1px !important;
  }

  .ant-dropdown-button > .ant-btn:last-child {
    border-radius: 0 7px 7px 0 !important;
    padding: 0 6px !important;
    border-left-width: 1px !important;
    min-width: 0 !important;
    width: 22px !important;
  }
`;

const DropdownMenuStyles = createGlobalStyle`
  .${SCOPE_DROPDOWN_MENU_CLASS} .ant-dropdown-menu {
    min-width: 173px;
    border-radius: 10px;
    border: 1px solid #e9f6f7;
    box-shadow: 10px 10px 10px 0px #0000000d;
    padding: 12px !important;
  }

  .${SCOPE_DROPDOWN_MENU_CLASS} .ant-dropdown-menu-item {
    font-size: 14px !important;
    line-height: 20px !important;
    font-weight: 400 !important;
    padding: 4px 2px !important;
    border-radius: 4px;
  }

  .${SCOPE_DROPDOWN_MENU_CLASS} .ant-dropdown-menu-item:hover {
    background-color: var(--color-status-warning-bg, #fff5e2) !important;
    color: var(--color-text-dark, #042a2a) !important;
  }

  .${SCOPE_DROPDOWN_MENU_CLASS} .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:hover {
    background-color: var(--color-status-warning-bg, #fff5e2) !important;
    color: #b94a48 !important;
  }

  .${SCOPE_DROPDOWN_MENU_CLASS} .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:hover .anticon {
    color: #b94a48 !important;
  }

  .${SCOPE_DROPDOWN_MENU_CLASS} .ant-dropdown-menu-item-icon {
    font-size: 16px !important;
  }
`;

/** Save button used when a row is in edit mode (matches Edit dropdown styling). */
export const ScopeEditSaveButton = styled(IconButton)`
  &&& {
    height: 26px;
    min-width: 0;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    border-radius: 7px;
    border: 2px solid var(--color-border, #dbeae3);
    box-shadow: 0px 0px 10px 0px #0000000d;
    background-color: var(--color-surface-raised, #ffffff);
    color: var(--color-text-dark, #042a2a);
    display: inline-flex;
    align-items: center;

    &:hover,
    &:focus {
      color: var(--color-text-dark, #042a2a) !important;
      border-color: var(--color-primary, #41aaaf) !important;
    }
  }
`;

/** Cancel icon used when a row is in edit mode (next to Save). */
export const ScopeEditCancelIcon = styled(CloseCircleFilled)`
  &&& {
    font-size: 16px;
    color: var(--color-text-muted, #5a6a6a);
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.2s;

    &:hover {
      color: #d9534f;
    }
  }
`;

export interface ScopeDropdownButtonProps {
  /** Label of the main button (e.g. "Edit"). */
  label: React.ReactNode;
  /** Menu items for the dropdown (Edit, Delete, etc.). */
  menu: MenuProps;
  /** Called when the main button is clicked. */
  onClick?: () => void;
  /** Dropdown placement (e.g. 'bottomRight'). */
  placement?:
    | 'bottomLeft'
    | 'bottomRight'
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomCenter';
  /** Optional trigger. */
  trigger?: ('click' | 'hover')[];
  /** When true, button sizes to content (e.g. corner of a card). */
  inline?: boolean;
  /** Controlled open state (e.g. keep file card menu visible while open). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Reusable split button with dropdown, styled to match Scope Columns button look.
 * Use in tables (e.g. Transactions tab, Tax Rates tab) for row actions like Edit / Delete.
 */
export const ScopeDropdownButton: React.FC<ScopeDropdownButtonProps> = ({
  label,
  menu,
  onClick,
  placement = 'bottomRight',
  trigger = ['click'],
  inline = false,
  open,
  onOpenChange,
}) => (
  <>
    <DropdownMenuStyles />
    <Wrapper $inline={inline}>
      <Dropdown.Button
        overlayClassName={SCOPE_DROPDOWN_MENU_CLASS}
        menu={menu}
        icon={<DownOutlined style={{ fontSize: 10 }} />}
        onClick={onClick}
        trigger={trigger}
        placement={placement}
        open={open}
        onOpenChange={onOpenChange}
      >
        {label}
      </Dropdown.Button>
    </Wrapper>
  </>
);
